import { Button, Input, Tooltip } from '@/shared/components';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import './cart-promotion.scss';
import { useEffect, useMemo, useState } from 'react';
import { usePriceStore } from '@/store/price-store';
import { isEmpty } from 'lodash';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { useProductStore } from '../product-store';
import { useCartStore } from '../cart-store';

interface CartPromotionProps {
  className: string;
  toApplyCode: (promoCode: string) => void;
  toClearPromotionInfo: () => void;
}

export const CartPromotion: React.FC<CartPromotionProps> = ({ className, toApplyCode, toClearPromotionInfo }) => {
  const { t } = useLang(NAMESPACE.promotion);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { products } = useProductStore();
  const [promoCode, setPromoCode] = useState('');
  const { promotionInfo, promotionError, setPromotionError } = usePriceStore();
  const [errorMessage, setErrorMessage] = useState('');
  const { trackEventEffect } = useEventTracking();
  trackEventEffect(
    {
      key: 'offers',
      action: 'offerApplied',
      payload: {
        offerType: promotionInfo?.promoType,
        offerCode: promotionInfo?.promoCode,
      },
    },
    !isEmpty(promotionInfo) ? true : false
  );
  useEffect(() => {
    if (!promotionError) {
      setErrorMessage('');
      return;
    }
    let error = commonT(`code.${promotionError}`);
    if (error === `code.${promotionError}`) {
      error = commonT('promotion.apply.failed');
    }

    setErrorMessage(error);
  }, [promotionError]);

  useEffect(() => {
    if (products?.length === 0) {
      toClear();
    }
  }, [products]);

  useEffect(() => {
    toClear();
  }, [useCartStore.getState().address]);

  const toClear = () => {
    setPromoCode('');
    setPromotionError(null);
    toClearPromotionInfo();
  };

  const applyCode = () => {
    setPromotionError(null);
    toApplyCode(promoCode.trim());
  };

  const handleChange = (event) => {
    setPromoCode(event.target.value);
    setPromotionError(null);
  };

  const applyDisabled = useMemo(() => {
    return isEmpty(promoCode?.trim());
  }, [promoCode]);

  return (
    <div className={className}>
      <div className="border border-solid border-gray-450 rounded px-6 pt-6 pb-8 flex flex-col text">
        <h1 className="font-medium">{t('promotion.block.title')}</h1>
        <div className="pt-6 pb-2 flex flex-row justify-start items-center">
          <span>{t('promotion.block.tooltip.title')}:</span>
          <Tooltip
            title={t('promotion.block.tooltip')}
            placement="top"
            maxWidth={300}
            className="flex justify-center items-center"
          >
            <span className="icon-16-info-bold text-[#0071D0] text-xs pl-2"></span>
          </Tooltip>
        </div>
        <div className="grid grid-cols-[1fr_auto]">
          <Input
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                applyCode();
              }
            }}
            value={promoCode}
            className="sm:w-full md:w-full lg:w-full min-w-0 w-full"
            onChange={handleChange}
            validation={{ message: errorMessage }}
          />
          <Button disabled={applyDisabled} onClick={applyCode} className="box-border rounded-s-none">
            {commonT('apply')}
          </Button>
        </div>

        {!isEmpty(promotionInfo) && promotionInfo.promoCode && (
          <div className="flex flex-row justify-between items-center pt-6 min-h-[90px]">
            <div className="wrap_contain w-[calc(100%-56px)]">
              <div className="h-[92px] flex flex-col justify-around items-start rounded-lg border-2 border-dashed border-[#FB9090] bg-[#FFEEEE] px-5 py-2">
                <span className="font-bold text-[#E71316] text-lg">{promotionInfo.promoCode}</span>
                <span className="custom-clamp-2 text-sm text-[#222] block text-ellipsis line-clamp-2 overflow-hidden">
                  {promotionInfo.promoType === 'FWO' ? (
                    <div>
                      <Tooltip title={promotionInfo.promoDesc} htmlTitle={promotionInfo.promoDesc} placement="top">
                        <div
                          className="flex text-ellipsis overflow-hidden whitespace-nowrap h-[25px] max-w-[200px] fwo-promo-desc"
                          dangerouslySetInnerHTML={{
                            __html:
                              (promotionInfo.promoDiscount ? promotionInfo.promoDiscount + '%' : '0%') +
                              ' ' +
                              promotionInfo.promoDesc,
                          }}
                        ></div>
                      </Tooltip>
                    </div>
                  ) : (
                    promotionInfo?.promoName
                  )}
                </span>
              </div>
              <div className="wrap_right_top"></div>
              <div className="wrap_right_bottom"></div>
            </div>
            <div className="wrap_contain w-[56px]">
              <span
                className="icon-32-close-bold flex justify-center items-center text-[#3D3C3C] cursor-pointer min-h-[92px] rounded-lg border-y-2 border-r-2 border-l-0 border-dashed border-[#FB9090] bg-[#FFEEEE] py-2"
                onClick={toClear}
              ></span>
              <div className="wrap_left_top"></div>
              <div className="wrap_left_bottom"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
